import { useEffect, useState, useCallback } from "react";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import CoachProfileCard from "../components/CoachProfileCard";
import CoachOnboardingWizard from "../components/CoachOnboardingWizard";
import CoachNotesPanel from "../components/CoachNotesPanel";
import CoachPaymentSetupCard from "../components/CoachPaymentSetupCard";
import ClientActivityFeed from "../components/ClientActivityFeed";
import CoachChatPanel from "../components/CoachChatPanel";
import CoachReviewsCard from "../components/CoachReviewsCard";
import CoachAnalyticsCard from "../components/CoachAnalyticsCard";
import CoachBookingSlotsCard from "../components/CoachBookingSlotsCard";
import AdminGlobalCoachingView from "../components/AdminGlobalCoachingView";
import ProductTour from "../components/ProductTour";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { Navigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Users, ShieldCheck, ChevronRight, Syringe, Utensils, Scale, Pill, Mail, ArrowLeft, GraduationCap } from "lucide-react";

const TEAL = "#0D9488";

const COACH_TOUR_STEPS = [
  {
    placement: "center",
    title: "Welcome to your Coach hub",
    body: "60-second tour of the coach tools. Use ← → to navigate, Esc to exit anytime.",
  },
  {
    selector: '[data-testid="page-hero"]',
    placement: "bottom",
    title: "My Clients",
    body: "Everyone who has shared their progress with you shows up on this page — read-only, always. Search by handle or name to find someone fast.",
  },
  {
    selector: '[data-testid="coach-profile-card"]',
    placement: "bottom",
    title: "Your public coach profile",
    body: "Edit your bio, specialties, and photo. This is what shows on your public /coach/:handle page and inside the Coach Directory.",
  },
  {
    selector: '[data-testid="coach-booking-slots-card"]',
    placement: "top",
    title: "Booking slots",
    body: "Publish date/time/price slots. Clients book from your public profile, both parties get a 30-min Resend reminder before it starts.",
  },
  {
    selector: '[data-testid="coach-reviews-card"]',
    placement: "top",
    title: "Ratings & reviews",
    body: "5-star reviews float to the top of your public page. Reply to any review — your response shows publicly under the star.",
  },
  {
    selector: '[data-testid="coach-analytics-card"]',
    placement: "top",
    title: "Coach analytics",
    body: "Client activity heatmap, rating breakdown, engagement metrics. All private — only you see these.",
  },
  {
    placement: "center",
    title: "Deep dive?",
    body: 'Every feature is documented step-by-step in Tutorial → "For coaches" tab. You can retake this tour anytime.',
  },
];

export default function MyClients() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const coachingEnabled = (settings?.feature_flags?.coaching ?? true) !== false;
  const isAdmin = user?.role === "admin";
  const [state, setState] = useState({ loading: true, status: null, clients: [] });
  const [selected, setSelected] = useState(null);
  const [overview, setOverview] = useState(null);
  const [ovLoading, setOvLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitial, setWizardInitial] = useState({});
  const [allowedSpecialties, setAllowedSpecialties] = useState([]);
  const [tourOpen, setTourOpen] = useState(false);
  const openTour = useCallback(() => setTourOpen(true), []);
  const closeTour = useCallback(() => setTourOpen(false), []);

  useEffect(() => {
    api.get("/coach/clients").then(({ data }) => {
      setState({ loading: false, status: data.status, clients: data.clients || [] });
    }).catch(() => setState({ loading: false, status: "error", clients: [] }));
  }, []);

  // First-time onboarding trigger (verified coach who hasn't finished the wizard)
  const handleProfileLoad = (data) => {
    setAllowedSpecialties(data?.allowed_specialties || []);
    if (data && data.onboarded === false) {
      setWizardInitial(data);
      setWizardOpen(true);
    }
  };

  const openClient = async (c) => {
    setSelected(c);
    setOverview(null);
    setOvLoading(true);
    try {
      const { data } = await api.get(`/coach/clients/${c.user_id}/overview`);
      setOverview(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOvLoading(false);
    }
  };

  if (!coachingEnabled) return <Navigate to="/dashboard" replace />;

  return (
    <AppLayout>
      <PageHero
        eyebrow={isAdmin ? "Coach hub · Admin" : "Coach hub"}
        title="My Clients"
        icon={<Users className="w-10 h-10 text-[#0D9488]" />}
        subtitle={isAdmin
          ? "Support view of every coach + every client on the platform."
          : "Read-only view of everyone who has shared their progress with you."}
      />

      {/* Coach tour + Tutorial links */}
      {!isAdmin && state.status === "active" && (
        <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="coach-tour-strip">
          <button
            type="button"
            onClick={openTour}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-teal-200 bg-teal-50 hover:bg-teal-100 text-teal-800 text-[12px] font-semibold transition-colors"
            data-testid="coach-tour-open"
          >
            <GraduationCap className="w-3.5 h-3.5" /> Take the coach tour
          </button>
          <Link
            to="/tutorial"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-50 text-[12px] font-semibold transition-colors"
            data-testid="coach-tour-tutorial-link"
          >
            Full coach tutorial <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {isAdmin && <AdminGlobalCoachingView />}

      {state.loading && <div className="h-40 bg-white border border-slate-200 rounded-xl animate-pulse mt-6" />}

      {!state.loading && state.status === "active" && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CoachProfileCard onProfileLoad={handleProfileLoad} />
          <CoachPaymentSetupCard />
        </div>
      )}

      {!state.loading && state.status === "active" && (
        <div className="mt-6">
          <CoachAnalyticsCard />
        </div>
      )}

      {!state.loading && state.status === "active" && (
        <div className="mt-6">
          <CoachBookingSlotsCard />
        </div>
      )}

      {!state.loading && state.status === "active" && (
        <div className="mt-6">
          <ClientActivityFeed />
        </div>
      )}

      {!state.loading && state.status === "active" && (
        <div className="mt-6">
          <CoachReviewsCard />
        </div>
      )}

      <CoachOnboardingWizard
        open={wizardOpen}
        initial={wizardInitial}
        allowed={allowedSpecialties}
        onClose={() => setWizardOpen(false)}
      />

      {!state.loading && !isAdmin && state.status === "not_verified" && (
        <div className="mt-6 border border-amber-200 bg-amber-50/40 rounded-xl p-8 text-center" data-testid="clients-not-verified">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-amber-600" />
          <div className="font-display font-bold text-lg text-slate-900 mb-2">You've been invited as a coach</div>
          <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
            One or more clients have granted you access, but your account isn't marked as a verified coach yet.
            Please contact your site admin to enable your coach status — they can do it in one click from the
            Admin → Users panel.
          </p>
        </div>
      )}

      {!state.loading && !isAdmin && state.status === "no_invites" && (
        <div className="mt-6 border border-dashed border-slate-200 rounded-xl p-10 text-center bg-slate-50/40" data-testid="clients-empty">
          <Mail className="w-8 h-8 mx-auto mb-2 text-slate-400" />
          <div className="font-display font-bold text-lg text-slate-900 mb-1">No clients yet</div>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Share your email with the clients you coach — they'll grant you access from their Profile page.
          </p>
        </div>
      )}

      {!state.loading && state.status === "active" && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Client list */}
          <aside className="bg-white border border-slate-200 rounded-xl p-4 space-y-1" data-testid="clients-list">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 px-2 pb-1">
              {state.clients.length} active client{state.clients.length === 1 ? "" : "s"}
            </div>
            {state.clients.map((c) => (
              <button
                key={c.user_id}
                type="button"
                onClick={() => openClient(c)}
                className={`w-full text-left p-3 rounded-lg flex items-center gap-3 transition-colors ${
                  selected?.user_id === c.user_id
                    ? "bg-teal-50 border border-teal-200"
                    : "border border-transparent hover:bg-slate-50 hover:border-slate-200"
                }`}
                data-testid={`client-row-${c.user_id}`}
              >
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-xs text-white shrink-0"
                     style={{ background: "linear-gradient(135deg,#CBD5E1 0%,#94A3B8 100%)" }}>
                  {(c.handle || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">@{c.handle}</div>
                  <div className="text-[11px] text-slate-500 truncate">{c.email}</div>
                </div>
                <ChevronRight className={`w-4 h-4 ${selected?.user_id === c.user_id ? "text-teal-600" : "text-slate-300"}`} />
              </button>
            ))}
          </aside>

          {/* Overview */}
          <section data-testid="client-overview">
            {!selected && (
              <div className="border border-dashed border-slate-200 rounded-xl p-10 text-center bg-slate-50/40 h-full flex flex-col items-center justify-center">
                <ArrowLeft className="w-6 h-6 mb-2 text-slate-400" />
                <div className="text-sm text-slate-500">Pick a client from the list to view their logs.</div>
              </div>
            )}
            {selected && ovLoading && (
              <div className="h-64 bg-white border border-slate-200 rounded-xl animate-pulse" />
            )}
            {selected && overview && (
              <div className="space-y-6">
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm text-white"
                         style={{ background: "linear-gradient(135deg,#CBD5E1 0%,#94A3B8 100%)" }}>
                      {(overview.client.handle || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-display font-bold text-xl text-slate-900">@{overview.client.handle}</div>
                      <div className="text-xs text-slate-500">{overview.client.email}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                    <Stat icon={Scale} label="Body weight" value={overview.client.body_weight ? `${overview.client.body_weight} ${overview.client.weight_unit || "lb"}` : "—"} />
                    <Stat icon={Scale} label="Goal weight" value={overview.client.goal_weight ? `${overview.client.goal_weight} ${overview.client.weight_unit || "lb"}` : "—"} />
                    <Stat icon={Syringe} label="Shots" value={overview.shots.length} />
                    <Stat icon={Utensils} label="Meals" value={overview.nutrition_logs.length} />
                  </div>
                </div>

                <LogSection
                  title="Recent shots"
                  icon={Syringe}
                  rows={overview.shots}
                  testid="client-shots"
                  render={(r) => `${r.peptide_name} · ${r.dose_amount ?? r.dose ?? "?"} ${r.dose_unit || ""} · ${(r.taken_at || "").slice(0, 10)}`}
                />
                <LogSection
                  title="Recent nutrition"
                  icon={Utensils}
                  rows={overview.nutrition_logs}
                  testid="client-nutrition"
                  render={(r) => `${r.note || "meal"} — ${Math.round(r.calories || 0)} kcal · ${(r.logged_at || "").slice(0, 10)}`}
                />
                <LogSection
                  title="Weight log"
                  icon={Scale}
                  rows={overview.weights}
                  testid="client-weights"
                  render={(r) => `${r.weight} ${r.unit || "lb"} · ${(r.logged_at || "").slice(0, 10)}`}
                />
                <LogSection
                  title="Supplements"
                  icon={Pill}
                  rows={overview.supplements}
                  testid="client-supplements"
                  render={(r) => `${r.name}${r.time_hhmm ? " · " + r.time_hhmm : ""}`}
                />

                <CoachNotesPanel clientId={overview.client.id} clientHandle={overview.client.handle} />

                {/* Live activity feed for this specific client */}
                <ClientActivityFeed clientId={overview.client.id} />

                {/* Chat thread with this client */}
                {selected?.share_id && (
                  <CoachChatPanel
                    shareId={selected.share_id}
                    counterpartyLabel={"@" + (overview.client.handle || "client")}
                    defaultOpen
                  />
                )}
              </div>
            )}
          </section>
        </div>
      )}

      <ProductTour steps={COACH_TOUR_STEPS} open={tourOpen} onClose={closeTour} />
    </AppLayout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: TEAL }} />
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="text-sm font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function LogSection({ title, icon: Icon, rows, render, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: TEAL }} />
        <h3 className="font-display font-bold text-slate-900">{title}</h3>
        <span className="text-[10px] text-slate-400 ml-1 tabular-nums">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No entries yet.</div>
      ) : (
        <ul className="space-y-1 text-sm text-slate-700 max-h-64 overflow-auto">
          {rows.slice(0, 20).map((r) => (
            <li key={r.id} className="border-b border-slate-100 last:border-0 py-1.5">{render(r)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
